import { AdditionalServicesService } from '@additional-services/services/additional-services.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CompleteOrderBackendService } from '@complete-order/services/complete-order-backend.service';
import { CompleteOrderService } from '@complete-order/services/complete-order.service';
import {
  DeliveryDates,
  DeliveryDatesRequest,
  ErrorType,
  OfferDeliveryDates,
  OffersType,
} from '@dgx-pr-spa/shared/util';
import { BuildConfigService } from '@dgx-pr-spa/ui-components';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ProductDetailsService } from '@product-details/services/product-details.service';
import { offersType } from '@shared/helpers/function.helpers';
import { ViewOffersService } from '@view-offers/services/view-offers.service';
import * as fromOffersDeliveryDates from '@view-offers/store/actions/offers.delivery.dates.action';
import * as fromOffersDeliveryDatesReducer from '@view-offers/store/reducers/offers.delivery.dates.reducer';
import { EMPTY, of } from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import * as fromSelectedReplacement from '../actions/replacement.delivery.dates.action';

@Injectable()
export class DeliveryDatesEffects {
  constructor(
    public readonly actions$: Actions,
    public readonly completeOrderBackend: CompleteOrderBackendService,
    public readonly completeOrderService: CompleteOrderService,
    public readonly additionalServicesService: AdditionalServicesService,
    public readonly productDetailsService: ProductDetailsService,
    public readonly buildConfigService: BuildConfigService,
    public readonly router: Router,
    public readonly viewOffersService: ViewOffersService,
    public readonly store?: Store<fromOffersDeliveryDatesReducer.OffersDeliveryDatesState>
  ) {}

  loadDeliveryDates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSelectedReplacement.loadDeliveryDates),
      withLatestFrom(
        this.completeOrderService.createOrderLoaded$,
        this.productDetailsService.deliveryDatesLoaded$,
        this.additionalServicesService.getDelivery$
      ),
      filter(
        ([, loaded, datesLoaded, delivery]) =>
          !loaded && !datesLoaded && !!delivery
      ),
      switchMap(([action]) =>
        this.productDetailsService.getDeliveryDatesRequestArgs$.pipe(
          first(),
          map((args) => ({ action, args }))
        )
      ),
      mergeMap(({ action, args }) =>
        this.viewOffersService
          .selectOffersDeliveryDatesById$(`${args.supplier_id}/${args.cat_num}`)
          .pipe(
            first(),
            map((entityDates) => ({ action, args, entityDates }))
          )
      ),

      mergeMap(
        ({
          action,
          args,
          entityDates,
        }: {
          action: ReturnType<typeof fromSelectedReplacement.loadDeliveryDates>;
          args: DeliveryDatesRequest;
          entityDates: OfferDeliveryDates;
        }) => {
          const { offer_reference, supplier_id, cat_num } = args;
          const service_cat_numbers =
            action.payload?.service_cat_numbers ?? null;
          const hasServiceExtras = !!service_cat_numbers?.length;
          const payload: DeliveryDatesRequest = {
            ...args,
            service_cat_numbers: hasServiceExtras ? service_cat_numbers : null,
          };

          if (!offer_reference) {
            return EMPTY;
          }

          if (!hasServiceExtras && entityDates?.data) {
            return of(
              fromSelectedReplacement.loadDeliveryDatesSuccess({
                payload: entityDates.data as DeliveryDates,
              })
            );
          }

          return this.completeOrderBackend
            .fetchDeliveryDates(
              `${this.buildConfigService.config.pr}/v2/offers/deliverydates`,
              payload
            )
            .pipe(
              switchMap((response) => [
                fromSelectedReplacement.loadDeliveryDatesSuccess({
                  payload: response as DeliveryDates,
                }),
                fromOffersDeliveryDates.addOfferDeliveryDates({
                  payload: {
                    data: response as DeliveryDates,
                    supplier_id,
                    cat_num,
                  },
                }),
              ]),
              catchError((error) =>
                of(
                  fromSelectedReplacement.triggerDeliveryApiError({
                    payload: {
                      url: error.url,
                      status: error.status,
                      statusText: error.error.status,
                      delivery_dates: [],
                      message: error.error.message,
                    },
                  })
                )
              )
            );
        }
      )
    )
  );

  handleDeliveryDatesApiFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromSelectedReplacement.handleDeliveryApiError),
        withLatestFrom(this.productDetailsService.selected$),
        filter(([, selected]) => !!selected.supplier_id && !!selected.cat_num),
        tap(([{ payload }, selected]) => {
          const { url } = payload;
          const urlParams = new URLSearchParams(url);
          const supplierId = urlParams.get('supplierId') || '';
          const catNum = urlParams.get('catNum');

          if (
            supplierId === selected?.supplier_id?.toString() &&
            catNum === selected.cat_num
          ) {
            this.productDetailsService.triggerDeliveryApiError(payload);
          }
        })
      ),
    { dispatch: false }
  );

  handleDeliveryDatesResponse$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          fromSelectedReplacement.loadDeliveryDatesSuccess,
          fromSelectedReplacement.triggerDeliveryApiError
        ),
        withLatestFrom(this.viewOffersService.offers$),
        tap(([{ payload }, offers]) => {
          const { delivery_dates, message, status, statusText } = payload;
          const type = offersType(offers);
          let msg = `${status || ''} ${statusText || ''} `;

          if (
            !delivery_dates?.length &&
            message !== ErrorType.SUPPLIER_TO_ARRANGE
          ) {
            if (message === ErrorType.DELIVERY_API_ERROR) {
              msg += ErrorType.BESPOKE_DELIVERY_UNAVILABLE;

              if (type === OffersType.BESPOKE) {
                this.router.navigate(['/error'], {
                  queryParams: { msg },
                  replaceUrl: true,
                });
              } else if (type === OffersType.STANDARD) {
                this.productDetailsService.loadDeliveryDatesFail(msg);
              }

              return;
            }

            // Delivery is unavailable, show an error dialog in here!
            this.productDetailsService.loadDeliveryDatesFail(msg);
          }
        })
      ),
    { dispatch: false }
  );
}
